import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useContent, useLang } from "../i18n/LanguageContext";
import { ShieldCheck, ArrowRight, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountModal({ open, onOpenChange }) {
  const c = useContent();
  const { lang } = useLang();
  const navigate = useNavigate();
  const conditions = c.conditions;
  const t = c.accountModal;
  const [form, setForm] = useState({ name: "", email: "", term: conditions.rows[1].term, amount: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // refNo string
  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const reset = () => {
    setForm({ name: "", email: "", term: conditions.rows[1].term, amount: "" });
    setResult(null);
    setBusy(false);
  };

  const closeModal = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(reset, 250);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !emailValid(form.email)) {
      toast.error(t.toastErr);
      return;
    }
    // Take the customer straight into account creation -> immediate dashboard access
    onOpenChange(false);
    setTimeout(reset, 250);
    navigate("/register", {
      state: { name: form.name, email: form.email, term: form.term, amount: form.amount },
    });
  };

  const copyRef = () => {
    if (result) {
      navigator.clipboard?.writeText(result);
      toast.success(t.copied);
    }
  };

  const inputCls =
    "w-full rounded-md border border-[#e7e2d8] bg-white px-4 py-3 text-sm text-[#16233d] placeholder-[#9aa0ad] focus:outline-none focus:border-[#b08d57] focus:ring-2 focus:ring-[#b08d57]/20 transition";

  return (
    <Dialog open={open} onOpenChange={closeModal}>
      <DialogContent className="sm:max-w-[460px] bg-white" onOpenAutoFocus={(e) => e.preventDefault()}>
        {result ? (
          <div className="text-center py-2">
            <div className="w-14 h-14 rounded-full bg-[#eaf6ec] flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={28} className="text-[#3a9d5d]" />
            </div>
            <DialogTitle className="font-serif-lux text-2xl text-[#16233d]">{t.successTitle}</DialogTitle>
            <DialogDescription className="text-[#5c6577] mt-2">{t.successText}</DialogDescription>
            <div className="mt-5 rounded-xl border border-[#e7e2d8] bg-[#faf8f4] px-5 py-4">
              <div className="text-xs text-[#8a8f9c] mb-1">{t.refLabel}</div>
              <button onClick={copyRef} className="inline-flex items-center gap-2 font-mono text-2xl text-[#16233d] hover:text-[#8f6f3f] transition-colors">
                {result} <Copy size={16} className="text-[#b08d57]" />
              </button>
            </div>
            <p className="text-xs text-[#8a8f9c] mt-4">{t.successNote}</p>
            <button onClick={() => closeModal(false)} className="btn-gold rounded-md w-full py-3.5 font-medium mt-6">
              {t.done}
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="w-12 h-12 rounded-lg bg-[#faf3e8] flex items-center justify-center mb-2">
                <ShieldCheck size={24} className="text-[#b08d57]" />
              </div>
              <DialogTitle className="font-serif-lux text-2xl text-[#16233d]">{t.title}</DialogTitle>
              <DialogDescription className="text-[#5c6577]">
                {t.desc}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4 mt-2">
              <input name="name" value={form.name} onChange={handle} placeholder={t.name} className={inputCls} />
              <input name="email" type="email" value={form.email} onChange={handle} placeholder={t.email} className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <select name="term" value={form.term} onChange={handle} className={inputCls}>
                  {conditions.rows.map((r) => (
                    <option key={r.term} value={r.term}>{r.term} · {r.rate.toFixed(2)}%</option>
                  ))}
                </select>
                <input name="amount" value={form.amount} onChange={handle} placeholder={t.amount} className={inputCls} />
              </div>
              <button type="submit" disabled={busy} className="btn-gold rounded-md w-full py-3.5 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {t.submit} <ArrowRight size={17} />
              </button>
              <p className="text-xs text-[#8a8f9c] text-center">{t.note}</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
